import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppConfig } from "@/hooks/app-config/use-app-config";
import {
  useGetAllFormTemplates,
  useGetAllProcessTemplates,
  useUseFormTemplate,
  useDeleteFormTemplate,
  useDeleteProcessTemplate,
} from "@/hooks/template";
import { useAuthStore } from "@/store/use-auth-store";
import type { FormTemplate, ProcessTemplate } from "@/services/template";
import {
  TemplateCard,
  UseProcessTemplateModal,
} from "@/components/template";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

export default function TemplatesBrowser() {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const { data: appConfig } = useAppConfig();
  const { data: formTemplates, isLoading: loadingForms, isError: formsError } = useGetAllFormTemplates();
  const { data: processTemplates, isLoading: loadingProcesses, isError: processesError } = useGetAllProcessTemplates();

  const formsUnavailable = formsError || !Array.isArray(formTemplates);
  const processesUnavailable = processesError || !Array.isArray(processTemplates);
  const useFormMutation = useUseFormTemplate();
  const deleteFormMutation = useDeleteFormTemplate();
  const deleteProcessMutation = useDeleteProcessTemplate();

  const [selectedProcessTemplate, setSelectedProcessTemplate] =
    useState<ProcessTemplate | null>(null);

  const isPublisher = appConfig?.isTemplatePublisher ?? false;

  const handleUseForm = (template: FormTemplate) => {
    if (!user) return;
    useFormMutation.mutate({
      id: template.id,
      data: { creatorId: user.id, name: template.name },
    });
  };

  return (
    <div className="p-6">
      <h1 className="text-2xl font-semibold mb-6">{t("templates.title")}</h1>

      <Tabs defaultValue="forms">
        <TabsList className="mb-6">
          <TabsTrigger value="forms">{t("templates.formTemplates")}</TabsTrigger>
          <TabsTrigger value="processes">
            {t("templates.processTemplates")}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="forms">
          {loadingForms ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="h-40 bg-slate-100 rounded-lg animate-pulse" />
              ))}
            </div>
          ) : formsUnavailable ? (
            <div className="text-center py-16 text-slate-500">
              <p className="text-lg font-medium">{t("templates.loadError")}</p>
              <p className="text-sm mt-1">{t("templates.loadErrorDescription")}</p>
            </div>
          ) : formTemplates.length === 0 ? (
            <div className="text-center py-16 text-slate-500">
              <p className="text-lg font-medium">{t("templates.noTemplates")}</p>
              <p className="text-sm mt-1">{t("templates.noTemplatesDescription")}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {formTemplates.map((template) => (
                <TemplateCard
                  key={template.id}
                  template={template}
                  type="form"
                  isPublisher={isPublisher}
                  onUse={() => handleUseForm(template)}
                  onDelete={() => deleteFormMutation.mutate(template.id)}
                />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="processes">
          {loadingProcesses ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="h-40 bg-slate-100 rounded-lg animate-pulse" />
              ))}
            </div>
          ) : processesUnavailable ? (
            <div className="text-center py-16 text-slate-500">
              <p className="text-lg font-medium">{t("templates.loadError")}</p>
              <p className="text-sm mt-1">{t("templates.loadErrorDescription")}</p>
            </div>
          ) : processTemplates.length === 0 ? (
            <div className="text-center py-16 text-slate-500">
              <p className="text-lg font-medium">{t("templates.noTemplates")}</p>
              <p className="text-sm mt-1">{t("templates.noTemplatesDescription")}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {processTemplates.map((template) => (
                <TemplateCard
                  key={template.id}
                  template={template}
                  type="process"
                  isPublisher={isPublisher}
                  onUse={() => setSelectedProcessTemplate(template)}
                  onDelete={() => deleteProcessMutation.mutate(template.id)}
                />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {selectedProcessTemplate && (
        <UseProcessTemplateModal
          isOpen={true}
          onClose={() => setSelectedProcessTemplate(null)}
          templateId={selectedProcessTemplate.id}
          templateName={selectedProcessTemplate.name}
        />
      )}
    </div>
  );
}
